import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchUserRoles,
  fetchUserRole,
  createUserRole,
  updateUserRole,
} from "@/api/userRoles";
import AdminLayout from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Save, ShieldAlert } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { UserRole } from "@/types";

const UserRoleForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuth();
  const [permissionDialogOpen, setPermissionDialogOpen] = useState(false);

  const isNewRole = !id || id === "new";
  const title = isNewRole ? "Add User Role" : "Edit User Role";
  const roleId = isNewRole ? null : parseInt(id!, 10);

  // Check if current user has admin privileges to manage roles
  const hasAdminPrivileges = (): boolean => {
    if (!currentUser || !currentUser.roles) return false;

    // Check if the user has any system admin role
    return currentUser.roles.some((role) => role.isSystem === true);
  };

  // Check for admin permission and show dialog if needed
  useEffect(() => {
    if (!hasAdminPrivileges()) {
      setPermissionDialogOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch all roles (for validation of duplicate names)
  const { data: roles = [] } = useQuery({
    queryKey: ["userRoles"],
    queryFn: fetchUserRoles,
  });

  // Default role state
  const defaultRole = {
    id: 0,
    name: "",
    description: "",
    isActive: true,
    isSystem: false,
  };

  // Form state
  const [role, setRole] = useState<UserRole>(defaultRole);
  const [errors, setErrors] = useState({
    name: "",
  });
  const [isSaving, setIsSaving] = useState(false);

  // If editing, fetch current role data
  useEffect(() => {
    const loadRole = async () => {
      if (!isNewRole && roleId) {
        try {
          const roleData = await fetchUserRole(roleId);
          if (roleData) {
            setRole(roleData);

            // Redirect if it's SuperAdmin or Standard User role
            if (roleData.id === 1 || roleData.id === 5) {
              toast({
                title: `${roleData.name} Role`,
                description: `${roleData.name} role cannot be modified.`,
                variant: "destructive",
              });
              navigate("/admin/system/user-roles");
              return;
            }
          } else {
            // Role not found, redirect to roles list
            toast({
              title: "Role not found",
              description: "The requested role does not exist.",
              variant: "destructive",
            });
            navigate("/admin/system/user-roles");
          }
        } catch (error) {
          console.error("Error loading role:", error);
          toast({
            title: "Error loading role",
            description: "There was a problem loading the role details.",
            variant: "destructive",
          });
          navigate("/admin/system/user-roles");
        }
      }
    };

    loadRole();
  }, [roleId, isNewRole, navigate, toast]);

  // Handle input changes
  const handleInputChange = (field: string, value: string | boolean) => {
    setRole((prev) => ({ ...prev, [field]: value }));

    // Clear errors when user types
    if (field === "name" && errors.name) {
      setErrors((prev) => ({ ...prev, name: "" }));
    }
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors = { name: "" };
    let isValid = true;

    if (!role.name.trim()) {
      newErrors.name = "Role name is required";
      isValid = false;
    } else if (
      roles.some(
        (r) =>
          r.name.toLowerCase() === role.name.toLowerCase() && r.id !== role.id
      )
    ) {
      newErrors.name = "A role with this name already exists";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSaving(true);

    try {
      if (isNewRole) {
        // Create new role
        await createUserRole(role.name, role.description || undefined);
        toast({
          title: "Role created",
          description: `${role.name} has been created successfully.`,
        });
      } else if (roleId) {
        // Update existing role
        await updateUserRole(roleId, {
          name: role.name,
          description: role.description,
          isActive: role.isActive,
        });
        toast({
          title: "Role updated",
          description: `${role.name} has been updated successfully.`,
        });
      }

      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: ["userRoles"] });

      // Redirect back to user roles management
      navigate("/admin/system/user-roles");
    } catch (error) {
      console.error("Error saving role:", error);

      // Show error message
      let errorMessage = "An error occurred while saving the role";
      if (error instanceof Error) {
        errorMessage = error.message;
      }

      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <AdminLayout title={title}>
      <div className="mb-6">
        <Button variant="ghost" className="gap-2" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4" />
          Back to User Roles
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          <CardDescription>
            {isNewRole
              ? "Create a new role for users in the system."
              : "Update the details of an existing user role."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">
                  Role Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="name"
                  value={role.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  className={errors.name ? "border-destructive" : ""}
                  placeholder="e.g. Admin, Editor, Viewer"
                />
                {errors.name && (
                  <p className="text-sm text-destructive">{errors.name}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={role.description || ""}
                  onChange={(e) =>
                    handleInputChange("description", e.target.value)
                  }
                  placeholder="A description of this role's responsibilities and access level"
                  rows={3}
                />
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="isActive"
                  checked={role.isActive}
                  onCheckedChange={(checked) =>
                    handleInputChange("isActive", checked)
                  }
                />
                <Label htmlFor="isActive">Active</Label>
              </div>
            </div>

            <div className="flex justify-end gap-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(-1)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSaving} className="gap-2">
                {isSaving && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                )}
                <Save className="h-4 w-4" />
                Save Role
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Permission Dialog */}
      <Dialog
        open={permissionDialogOpen}
        onOpenChange={(open) => {
          setPermissionDialogOpen(open);
          if (!open) {
            navigate("/admin/system/user-roles");
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-amber-500" />
              Permission Required
            </DialogTitle>
            <DialogDescription>
              You need administrator privileges to manage user roles. This
              action is restricted to system administrators only.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              onClick={() => {
                setPermissionDialogOpen(false);
                navigate("/admin/system/user-roles");
              }}
            >
              Return to Roles
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default UserRoleForm;
